import classNames from 'classnames';
import React from 'react';

import Heading from '@jpp/atoms/Heading';
import { EIconType } from '@jpp/atoms/Icon/utils/types';
import { IconGroup } from '@jpp/molecules/IconGroup/IconGroup';
import SellBlock from '@jpp/molecules/SellBlock';
import { ESellBlockRenderedBy } from '@jpp/molecules/SellBlock/types';
import PDPCarousel from '@jpp/organisms/PDP/PDPCarousel';
import Container from '@jpp/shared/Grid/Container';
import Flex from '@jpp/shared/Grid/Flex';
import Row from '@jpp/shared/Grid/Row';
import Section from '@jpp/shared/Grid/Section';
import { IPdpPage } from 'common/redux/pdp/reducer';
import { EProductType } from 'common/typings/enums/product';

import './PDPHero.scss';

interface IPDPHeroProps {
  className?: string;
  pdp: IPdpPage;
}

const ROOT_CLASSNAME = 'PDPHero';

const getTitleText = (pdp: IPdpPage): string => {
  switch (pdp.pdp_type) {
    case EProductType.GiftCard:
      return 'E-Gift Card';
    default:
      return pdp.title;
  }
};

const PDPHero: React.FunctionComponent<IPDPHeroProps> = ({
  className,
  pdp,
  pdp: { image, featured_image, pdp_type },
}) => {
  const acf = pdp.acf_fields || pdp.acf;

  if (!acf) {
    return null;
  }

  const {
    image_gallery,
    default_product_range,
    default_product,
    beer_info,
    beer_features,
  } = acf;

  const defaultImage =
    image || default_product?.image || image_gallery?.[0].image;
  const { abv, style } = beer_info || {};
  const subHeading =
    abv && style
      ? `${style} <span class="${ROOT_CLASSNAME}__subheading-dot">•</span> ${abv}% ABV`
      : undefined;
  const preHeading = default_product_range?.name;
  const beerFeatures = beer_features;
  const isGiftCard = pdp_type === EProductType.GiftCard;
  const hasBeerFeatures =
    beerFeatures && beerFeatures?.length > 0 && !isGiftCard;
  const title = getTitleText(pdp);

  const titleContent = (
    <>
      <Heading
        priority={1}
        className={`${ROOT_CLASSNAME}__heading`}
        children={title}
      />

      {subHeading && (
        <Heading
          priority={2}
          className={`${ROOT_CLASSNAME}__subheading`}
          children={subHeading}
        />
      )}

      {preHeading && (
        <Heading
          priority={3}
          className={`${ROOT_CLASSNAME}__pre-heading`}
          children={preHeading}
        />
      )}
    </>
  );

  const beerIconGroup = (
    <div className={`${ROOT_CLASSNAME}__beer-features`}>
      {beerFeatures?.map((feature) => {
        if (!feature) {
          return null;
        }

        return (
          <IconGroup
            key={feature}
            icon={feature as EIconType}
            withName={true}
            inline={true}
            className={`${ROOT_CLASSNAME}__icon`}
          />
        );
      })}
    </div>
  );

  return (
    <Section
      className={classNames(ROOT_CLASSNAME, className, {
        [`${ROOT_CLASSNAME}--${pdp.pdp_type}`]: !!pdp.pdp_type,
      })}
      withPadding={false}
    >
      <Container>
        <Row>
          <Flex
            colLg={6}
            colMd={6}
            className={`${ROOT_CLASSNAME}__carousel-wrapper`}
          >
            <div
              className={classNames(
                `${ROOT_CLASSNAME}__title-content`,
                `${ROOT_CLASSNAME}__title-content--mobile`,
                {
                  [`${ROOT_CLASSNAME}__title-content--${pdp.pdp_type}`]:
                    !!pdp?.pdp_type,
                }
              )}
            >
              {titleContent}
            </div>

            {defaultImage && (
              <PDPCarousel
                className={`${ROOT_CLASSNAME}__carousel`}
                featuredImage={featured_image}
                image={defaultImage}
                gallery={image_gallery}
              />
            )}

            {hasBeerFeatures && beerIconGroup}
          </Flex>
          <Flex
            colLg={6}
            colMd={6}
            className={`${ROOT_CLASSNAME}__sell-block-wrapper`}
          >
            <div
              className={classNames(
                `${ROOT_CLASSNAME}__title-content`,
                `${ROOT_CLASSNAME}__title-content--desktop`,
                {
                  [`${ROOT_CLASSNAME}__title-content--${pdp.pdp_type}`]:
                    !!pdp?.pdp_type,
                }
              )}
            >
              {titleContent}
            </div>

            <SellBlock
              className={`${ROOT_CLASSNAME}__sell-block`}
              pdp={pdp}
              renderedBy={ESellBlockRenderedBy.PDPHero}
            />
          </Flex>
        </Row>
      </Container>
    </Section>
  );
};

export default PDPHero;
